import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";

interface Props {
  index: number;
}
export const LambdalabsNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const region = useCpFormValue(`lambdalabs.region`);
  const name = useCpFormValue(`lambdalabs.nodePools.${index}.name`);
  const instanceType = useCpFormValue(`lambdalabs.nodePools.${index}.instanceType`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`lambdalabs.nodePools`]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`lambdalabs.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`lambdalabs.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`lambdalabs.nodePools.${index}.name`} style={{ width: 600 }} />
                <CpErrorWarning name={`lambdalabs.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`lambdalabs.nodePools.${index}.instanceType`}>Instance Type</CpLabel>
                <Space.Compact>
                  <CpInput name={`lambdalabs.nodePools.${index}.instanceType`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("instanceType")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`lambdalabs.nodePools.${index}.instanceType`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`lambdalabs.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`lambdalabs.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`lambdalabs.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`lambdalabs.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[`lambdalabs.nodePools.${index}.minSize`, `lambdalabs.nodePools.${index}.maxSize`]}
                />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`lambdalabs.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`lambdalabs.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`lambdalabs.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "instanceType" ? (
        <NGProviderBrowser
          // TODO pagination for all discovery endpoints with pages
          title="Browse Lambda Labs Instance Types"
          initialValue={instanceType}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "live-instance-types",
                cluster: inputToMk8s(values),
              },
            });
            return Object.values(data.data).map((v: any) => ({
              ...v.instance_type,
              regions: v.regions_with_capacity_available || [],
            }));
          }}
          sortable
          onOk={(value) => {
            formSetValue(formId, `lambdalabs.nodePools.${index}.instanceType`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={["Name", "Cost per Month", "Availability", "Cores", "Memory", "Storage", "GPUs"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the instance type is currently available in selected region (${region})`,
            },
          ]}
          distinctFilters={["Cores", "Memory", "Storage", "GPUs"]}
          getData={(item: any) => {
            const priceCentsPerDay = Number(item.price_cents_per_hour || 0) * 24;
            const priceCentsPerMonth = priceCentsPerDay * 30;
            const price = priceCentsPerMonth / 100;
            const cost = price.toLocaleString("en-US", { style: "currency", currency: "USD" });
            const available = item.regions.some((r) => r.name === region);

            return [
              item.name,
              item.name,
              [price, cost],
              [available, available ? "Available" : "Not Available"],
              item.specs.vcpus,
              [item.specs.memory_gib, formatSizeForProviderBrowser(item.specs.memory_gib)],
              [item.specs.storage_gib, formatSizeForProviderBrowser(item.specs.storage_gib)],
              item.specs.gpus,
            ];
          }}
        />
      ) : null}
    </>
  );
};
